import { LocalStorageKeys } from "@/const/keys";
import { ReservationService } from "@/services/reservation-service/ReservationService";
import { create } from "zustand";

type State = {
  calendarVersions: CalendarVersion[];
  version?: Version;
};

type CalendarVersion = {
  calendarId: string;
  version: string;
  readAccessLevel: string;
};

type Version = {
  version: string;
  isOverrideReadAccess: boolean;
};

type Action = {
  clearCalendarVersions: () => void;
  setCalendarVersions: (calendarVersions: CalendarVersion[]) => void;
  initCalendarVersions: () => Promise<void>;
  isInvalidVersion: (calendarVersion: CalendarVersion) => boolean;
  setVersion: (version: Version) => void;
  toggleIsOverrideReadAccess: () => void;
  isInvalidMajorVersion: (version: Version) => boolean;
};

const versionManagerStore = create<State & Action>()((set, get) => ({
  calendarVersions: [],

  isInvalidMajorVersion: (version: Version) => {
    const { version: currentVersion } = get();

    if (!currentVersion) {
      localStorage.setItem(LocalStorageKeys.VERSION, JSON.stringify(version));
      set({ version: version });
      return false;
    }

    if (JSON.stringify(currentVersion) !== JSON.stringify(version)) {
      localStorage.setItem(LocalStorageKeys.VERSION, JSON.stringify(version));
      set({ version: version });
      return true;
    }

    return false;
  },

  toggleIsOverrideReadAccess: () => {
    const { version: currentVersion } = get();
    if (!currentVersion) {
      return;
    }

    currentVersion.isOverrideReadAccess = !currentVersion.isOverrideReadAccess;
    localStorage.setItem(LocalStorageKeys.VERSION, JSON.stringify(currentVersion));
    set({ version: currentVersion });
  },

  setVersion: (version: Version) => {
    set({ version: version });
  },

  clearCalendarVersions: () => {
    set({ calendarVersions: [] });
  },

  setCalendarVersions: (calendarVersions: CalendarVersion[]) => {
    set({ calendarVersions: calendarVersions });
  },

  initCalendarVersions: async () => {
    const reservationService = new ReservationService();

    const versionResponse = await reservationService.getCalendarEventsVersion();
    if (!versionResponse.data) {
      return;
    }

    const currentVersion = localStorage.getItem(LocalStorageKeys.VERSION);
    if (currentVersion) {
      set({ version: JSON.parse(currentVersion) });
    }

    set({
      calendarVersions: versionResponse.data.map((version) => ({
        calendarId: version.userId,
        version: version.version,
        readAccessLevel: version.readAccessLevel
      }))
    });
  },

  isInvalidVersion: (calendarVersion: CalendarVersion) => {
    const calendarVersionById = get().calendarVersions.find(
      (v) => v.calendarId === calendarVersion.calendarId
    );
    if (calendarVersionById) {
      return (
        calendarVersionById.readAccessLevel !== calendarVersion.readAccessLevel ||
        calendarVersionById.version !== calendarVersion.version
      );
    }

    return true;
  }
}));

export default versionManagerStore;
